import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Database,
  ExternalLink,
  Globe,
  History,
  Mail,
  MoreHorizontal,
  Pause,
  Receipt,
  RefreshCcw,
  UserPlus,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Money } from '../../components/Money';
import { Avatar } from '../../components/Avatar';
import { Tabs } from '../../components/Tabs';
import { Banner } from '../../components/Banner';
import { Modal } from '../../components/Modal';
import { Input } from '../../components/Input';
import { OptionCard } from '../../components/OptionCard';
import { InviteLinkResult } from '../../components/InviteLinkResult';
import { CreateOrInviteToggle, AccountCreatedPanel } from '../../components/CreateOrInvite';
import { CashPaymentModal } from '../../components/CashPaymentModal';
import { LedgerView } from '../../components/LedgerView';
import { formatDate } from '../../lib/format';
import type { ResellerCustomer } from '../../mocks/reseller';
import {
  useResellerCustomers,
  useCustomerDomains,
  useCustomerAudit,
  createResellerCustomerUser,
  type CustomerDomain,
  type CustomerAuditEvent,
  type ResellerInviteResult,
  type ResellerCreateUserResult,
} from '../../api/reseller';
import { useResellerCustomerLedger } from '../../api/billing';

const FALLBACK_CUSTOMER = {
  id: 'fallback',
  name: 'Customer',
  ownerEmail: 'owner@example.com',
  plan: 'Starter',
  status: 'active' as const,
  joinedAt: '2026-04-25',
  mrrMinor: 30000,
  isPastDue: false,
};

/** Business status → badge tone + label (mirrors the business status enum). */
const STATUS_BADGE: Record<string, { tone: 'success' | 'error' | 'warning' | 'info' | 'neutral'; label: string }> = {
  active: { tone: 'success', label: 'Active' },
  trial: { tone: 'info', label: 'Trial' },
  past_due: { tone: 'error', label: 'Past due' },
  pending_payment: { tone: 'warning', label: 'Pending payment' },
  provisioning: { tone: 'info', label: 'Provisioning' },
  suspended: { tone: 'warning', label: 'Suspended' },
  canceled: { tone: 'neutral', label: 'Canceled' },
  deleted: { tone: 'neutral', label: 'Deleted' },
};
function statusBadge(status: string) {
  return STATUS_BADGE[status] ?? { tone: 'neutral' as const, label: status };
}

/** Reseller-side customer detail with header card and 5 tabs. */
type Customer = ResellerCustomer | typeof FALLBACK_CUSTOMER;

export function CustomerDetail() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const { data: resellerCustomers = [] } = useResellerCustomers();
  const qc = useQueryClient();
  const [payOpen, setPayOpen] = useState(false);
  const customer: Customer =
    resellerCustomers.find((c) => c.id === id) ??
    resellerCustomers[0] ??
    FALLBACK_CUSTOMER;
  const realId = customer.id !== 'fallback' ? customer.id : '';
  const { data: ledger } = useResellerCustomerLedger(realId);
  const { data: domains } = useCustomerDomains(realId);
  const { data: audit } = useCustomerAudit(realId);
  const sb = statusBadge(customer.status);
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1200px] mx-auto">
      <Link
        to="/reseller/customers"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        All customers
      </Link>

      {/* Header card */}
      <Card className="p-5 sm:p-6 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <Avatar name={customer.name} size="xl" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                  {customer.name}
                </h1>
                <Badge tone={sb.tone}>{sb.label}</Badge>
                <span className="text-sm text-zinc-500 dark:text-zinc-400">·</span>
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{customer.plan}</span>
              </div>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                Owner <span className="font-medium text-zinc-700 dark:text-zinc-200">{customer.ownerEmail}</span>{' '}
                · joined <span className="tabular-nums">{formatDate(customer.joinedAt)}</span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" iconLeft={<ExternalLink className="w-4 h-4" />}>
              Open POS as user
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<Pause className="w-4 h-4" />}>
              Suspend
            </Button>
            <Button size="sm" iconLeft={<Receipt className="w-4 h-4" />} onClick={() => setPayOpen(true)}>
              Record cash payment
            </Button>
            <button
              type="button"
              className="w-9 h-9 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition border border-zinc-200 dark:border-zinc-700"
              aria-label="More"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Card>

      {/* Past-due banner — only when the customer is actually past due */}
      {customer.isPastDue && (
        <Banner
          tone="warning"
          title="Payment past due"
          action={
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm">
                Send dunning email
              </Button>
              <Button size="sm">Force renew (1 mo)</Button>
            </div>
          }
          className="mb-4"
        >
          The subscription auto-renewal hasn't been paid. Auto-suspend will trigger if payment isn't received.
        </Banner>
      )}

      <CashPaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        businessId={customer.id}
        businessName={customer.name}
        onRecorded={() => {
          void qc.invalidateQueries({ queryKey: ['reseller', 'customers'] });
          void qc.invalidateQueries({ queryKey: ['reseller', 'ledger', customer.id] });
          void qc.invalidateQueries({ queryKey: ['reseller', 'payments'] });
        }}
      />

      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview', content: <OverviewTab customer={customer} /> },
          { id: 'subscription', label: 'Subscription', content: <SubscriptionTab customer={customer} /> },
          { id: 'users', label: 'Users', content: <UsersTab businessId={customer.id} /> },
          { id: 'ledger', label: 'Ledger', count: ledger?.rows.length, content: <LedgerView ledger={ledger} /> },
          { id: 'domains', label: 'Domains', count: domains?.length, content: <DomainsTab domains={domains} /> },
          { id: 'audit', label: 'Audit', count: audit?.length, content: <AuditTab events={audit} /> },
        ]}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

function OverviewTab({ customer }: { customer: Customer }) {
  const sb = statusBadge(customer.status);
  return (
    <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
      <Card className="lg:col-span-2 p-5 sm:p-6">
        <CardHeader title="Status" />
        <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
          <Field label="Subscription">
            <Badge tone={sb.tone}>{sb.label}</Badge>
          </Field>
          <Field label="MRR">
            <Money minor={customer.mrrMinor} hideCents tone="neutral" className="font-medium text-zinc-950 dark:text-zinc-50" />
            <span className="text-xs text-zinc-500 dark:text-zinc-400 ml-1">/mo</span>
          </Field>
          <Field label="Plan">{customer.plan}</Field>
          <Field label="Next renewal">
            <span className="tabular-nums">{formatDate('2026-05-25')}</span>
          </Field>
          <Field label="Database">
            <span className="inline-flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />
              Managed (shared)
            </span>
          </Field>
          <Field label="Owner contact">{customer.ownerEmail}</Field>
        </dl>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Recent timeline" />
        <ul className="space-y-3 text-sm">
          <TimelineItem
            icon={<AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />}
            primary="Subscription expired"
            date="25 Apr"
          />
          <TimelineItem
            icon={<CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />}
            primary="Payment ৳300 (SSLCommerz)"
            date="25 Mar"
          />
          <TimelineItem
            icon={<CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />}
            primary="Payment ৳300 (SSLCommerz)"
            date="25 Feb"
          />
          <TimelineItem
            icon={<History className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" />}
            primary="Joined on Starter"
            date="25 Jan"
          />
        </ul>
      </Card>
    </div>
  );
}

function SubscriptionTab({ customer }: { customer: Customer }) {
  const sb = statusBadge(customer.status);
  return (
    <div className="space-y-4">
      <Card className="p-5 sm:p-6">
        <CardHeader
          title={
            <span className="flex items-center gap-2 flex-wrap">
              {customer.plan} ·{' '}
              <Money minor={customer.mrrMinor} hideCents tone="neutral" className="font-semibold" />
              <span className="text-sm font-normal text-zinc-500 dark:text-zinc-400">/ month</span>
            </span>
          }
          subtitle={
            <span className="tabular-nums">
              Started {formatDate(customer.joinedAt, { compact: true })} · Renews{' '}
              {formatDate('2026-05-25', { compact: true })}
            </span>
          }
          action={<Badge tone={sb.tone}>{sb.label}</Badge>}
        />
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Button variant="secondary" size="sm">Change plan</Button>
          <Button variant="secondary" size="sm">Apply coupon</Button>
          <Button variant="ghost" size="sm">Cancel</Button>
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Add-ons" subtitle="None active" />
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          Customer hasn't enabled any paid add-ons yet.
        </p>
      </Card>
    </div>
  );
}

/** Users tab — add owners/staff to this customer's POS (Feature #37). */
function UsersTab({ businessId }: { businessId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="p-5 sm:p-6">
      <CardHeader title="Users" subtitle="Add owners and staff to this customer's POS." />
      <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">
        Invite a teammate by email or create their account with a password now. Each user gets a POS
        role (Cashier, Manager, or Admin) for this business.
      </p>
      <Button iconLeft={<UserPlus className="w-4 h-4" />} onClick={() => setOpen(true)}>
        Add user
      </Button>
      <AddCustomerUserModal open={open} onClose={() => setOpen(false)} businessId={businessId} />
    </Card>
  );
}

/** Add a user to one of the reseller's customer businesses — mirrors the owner Team modal. */
function AddCustomerUserModal({
  open,
  onClose,
  businessId,
}: {
  open: boolean;
  onClose: () => void;
  businessId: string;
}) {
  const [action, setAction] = useState<'create' | 'invite'>('invite');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [posRole, setPosRole] = useState<'Cashier' | 'Manager' | 'Admin'>('Cashier');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResellerInviteResult | ResellerCreateUserResult | null>(null);

  function close() {
    setAction('invite');
    setEmail('');
    setName('');
    setPosRole('Cashier');
    setPassword('');
    setBusy(false);
    setError(null);
    setResult(null);
    onClose();
  }

  async function submit() {
    if (!email.trim() || !businessId) {
      setError('A valid email is required.');
      return;
    }
    if (action === 'create' && password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await createResellerCustomerUser(businessId, {
        action,
        email: email.trim(),
        name: name.trim() || undefined,
        posRole,
        password: action === 'create' ? password : undefined,
      });
      setResult(res);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(
        status === 409
          ? 'That person already has access to this business.'
          : status === 403
            ? 'You do not have permission to add users here.'
            : action === 'create'
              ? 'Could not create the account.'
              : 'Could not send the invite.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={result ? ('inviteUrl' in result ? 'Invitation sent' : 'Account created') : 'Add a user'}
      size="md"
      footer={
        result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!email.trim() || busy} loading={busy}>
              {action === 'create' ? 'Create account' : 'Send invite'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        'inviteUrl' in result ? (
          <InviteLinkResult email={result.email} inviteUrl={result.inviteUrl} />
        ) : (
          <AccountCreatedPanel email={result.email} />
        )
      ) : (
        <div className="space-y-4">
          <CreateOrInviteToggle action={action} onChange={setAction} />
          <Input
            label="Email"
            required
            type="email"
            placeholder="owner@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Full name"
            placeholder="(optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {action === 'create' && (
            <Input
              label="Password"
              required
              type="password"
              hint="Minimum 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              POS role <span className="text-rose-600">*</span>
            </span>
            <div className="grid grid-cols-3 gap-2">
              {(['Cashier', 'Manager', 'Admin'] as const).map((r) => (
                <OptionCard
                  key={r}
                  selected={posRole === r}
                  onSelect={() => setPosRole(r)}
                  className="text-center py-3"
                >
                  <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50">{r}</span>
                </OptionCard>
              ))}
            </div>
          </div>
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

/** DNS/TLS status → badge tone + label (mirrors the domains enum; falls back). */
const DNS_BADGE: Record<string, { tone: 'success' | 'warning' | 'error'; label: string }> = {
  verified: { tone: 'success', label: 'Verified' },
  pending: { tone: 'warning', label: 'Pending' },
  failed: { tone: 'error', label: 'Failed' },
};
const TLS_BADGE: Record<string, { tone: 'success' | 'warning' | 'error'; label: string }> = {
  issued: { tone: 'success', label: 'Issued' },
  pending: { tone: 'warning', label: 'Pending' },
  failed: { tone: 'error', label: 'Failed' },
};

function DomainsTab({ domains }: { domains?: CustomerDomain[] }) {
  const rows = domains ?? [];
  if (rows.length === 0) {
    return (
      <EmptyTab
        icon={<Globe className="w-5 h-5" />}
        title="No custom domains"
        body="This customer uses the default platform subdomain. Custom domains added for their business will appear here."
      />
    );
  }
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto nice-scroll">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
            <tr>
              <th className="text-left font-medium px-5 sm:px-6 py-3">Hostname</th>
              <th className="text-left font-medium px-4 py-3">DNS</th>
              <th className="text-left font-medium px-4 py-3">TLS</th>
              <th className="text-left font-medium px-5 sm:px-6 py-3">Primary</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {rows.map((d) => {
              const dns = DNS_BADGE[d.dnsStatus] ?? { tone: 'warning' as const, label: d.dnsStatus };
              const tls = TLS_BADGE[d.tlsStatus] ?? { tone: 'warning' as const, label: d.tlsStatus };
              return (
                <tr key={d.id}>
                  <td className="px-5 sm:px-6 py-4">
                    <span className="inline-flex items-center gap-2 mono text-xs text-zinc-700 dark:text-zinc-200">
                      <Globe className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />
                      {d.host}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <Badge tone={dns.tone}>{dns.label}</Badge>
                  </td>
                  <td className="px-4 py-4">
                    <Badge tone={tls.tone}>{tls.label}</Badge>
                  </td>
                  <td className="px-5 sm:px-6 py-4">
                    {d.isPrimary ? <Badge tone="info">Primary</Badge> : <span className="text-zinc-400 dark:text-zinc-500">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Audit display helpers — derive who/what/when defensively from the raw event. */
function auditWho(e: CustomerAuditEvent): string {
  const role = e.actorRole ? e.actorRole.split('_').pop() : null;
  if (e.actorName) return role ? `${e.actorName} (${role})` : e.actorName;
  if (e.actorEmail) return e.actorEmail;
  return 'system';
}
function auditWhat(action: string): string {
  const s = action.replace(/[._]/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Event';
}
function auditWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(iso, { compact: true })} · ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function AuditTab({ events }: { events?: CustomerAuditEvent[] }) {
  const rows = events ?? [];
  if (rows.length === 0) {
    return (
      <EmptyTab
        icon={<History className="w-5 h-5" />}
        title="No audit events yet"
        body="Account and security events for this business will appear here as they happen."
      />
    );
  }
  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-700">
        {rows.map((e) => {
          const failed = e.outcome === 'failure' || e.severity === 'critical' || e.severity === 'warning';
          return (
            <li key={e.id} className="flex items-start gap-3 px-5 sm:px-6 py-3.5">
              <span className="w-7 h-7 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex-shrink-0">
                {failed ? <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" /> : <RefreshCcw className="w-3.5 h-3.5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-950 dark:text-zinc-50">{auditWhat(e.action)}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  <span>{auditWho(e)}</span>
                  <span className="mx-1.5">·</span>
                  <span className="tabular-nums">{auditWhen(e.occurredAt)}</span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/** Shared honest empty state for the Domains / Audit tabs. */
function EmptyTab({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <Card className="p-10 text-center">
      <div className="w-11 h-11 mx-auto mb-3 grid place-items-center rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400">
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">{title}</h3>
      <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400 max-w-sm mx-auto">{body}</p>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-zinc-800 dark:text-zinc-100 inline-flex items-center gap-1.5 flex-wrap">
        {children}
      </dd>
    </div>
  );
}

function TimelineItem({
  icon,
  primary,
  date,
}: {
  icon: React.ReactNode;
  primary: string;
  date: string;
}) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-zinc-900 dark:text-zinc-100">{primary}</div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums mt-0.5">{date}</div>
      </div>
    </li>
  );
}

// suppress unused
void Mail;
